import React, { useCallback, useState } from 'react';

import CursorListBody from '../shared/cursor_list_body';
import { FilterBar, Segmented } from '../shared/controls';
import DataTable from '../shared/data_table';
import { Ledger, Note, StatusCell, What, Who } from '../shared/cells';
import StatusPill from '../shared/status_pill';
import TpDialog from '../shared/tp_dialog';
import useCursorList from '../shared/use_cursor_list';
import { discard_guard_text } from '../shared/dialog_logic';
import {
  fetch_payout_claims,
  fetch_payout_deleted_accounts,
  fetch_payout_held,
  post_payout_claim_resolve
} from '../api';
import {
  CLAIM_STATUSES,
  CLAIM_STATUS_DEFAULT,
  claims_filter_summary,
  claims_filters_on,
  REASON_MAX_LEN,
  claims_view,
  deleted_view,
  held_view,
  payout_error,
  payout_reason_error
} from './adapters';
import { claim_resolve_body } from './requests';
import LinesTable from './lines_table';
import ListCard, { shown_text } from './list_card';
import ResolveDialog from './resolve_dialog';
import { summary_line } from './summary_line';
import useIdempotentPost from './use_idempotent_post';
import DownloadCsvButton from '../../../util_components/csv_export/download_csv_button';
import { collect_all_pages } from '../../../util_components/csv_export/collect_pages';
import {
  CLAIM_COLUMNS,
  DELETED_COLUMNS,
  LINE_COLUMNS,
  auth_only,
  deleted_csv_rows,
  payouts_csv_name
} from './csv';

// ---- held lines ----

const read_held = (cursor) =>
  fetch_payout_held(cursor).then((d) => {
    const v = held_view(d);
    return { rows: v.rows, next: v.next_after_id };
  });

export function HeldPanel({ today, on_open_teacher, on_notify, on_error }) {
  const list = useCursorList(read_held, 'Could not load the held lines.', on_error, payout_error);
  const [line, setLine] = useState(null);
  const [stale, setStale] = useState('');

  return (
    <div className="tp-po-held">
      <p className="tp-po-lead">
        Bank lines the system could not settle on its own, oldest first. Check each one on the bank
        portal before deciding.
      </p>
      {stale ? (
        <div className="tp-banner tp-banner-warn" role="alert">
          {stale}
        </div>
      ) : null}
      <ListCard
        title="Held lines"
        sub={shown_text(list)}
        acts={
          <DownloadCsvButton
            columns={LINE_COLUMNS}
            load_rows={() => collect_all_pages(read_held)}
            file_name={payouts_csv_name('Held lines')}
            on_error={auth_only(payout_error, on_error)}
          />
        }
      >
        <CursorListBody
          list={list}
          render_rows={(rows) => (
            <LinesTable
              rows={rows}
              on_open_teacher={on_open_teacher}
              action={(r, className) => (
                <button
                  type="button"
                  className={className}
                  aria-label={`Resolve held line ${r.line_no}`}
                  onClick={() => setLine(r)}
                >
                  Resolve
                </button>
              )}
              empty_text="No bank lines are waiting for Ops."
            />
          )}
        />
      </ListCard>
      {line ? (
        <ResolveDialog
          line={line}
          today={today}
          on_close={() => setLine(null)}
          on_done={(text) => {
            setLine(null);
            setStale('');
            on_notify('success', text);
            list.reload();
          }}
          on_stale={(text) => {
            setLine(null);
            setStale(text);
            list.reload();
          }}
          on_error={on_error}
        />
      ) : null}
    </div>
  );
}

// ---- claims ----

function ClaimResolveDialog({ claim, on_close, on_done, on_stale, on_error }) {
  const [note, setNote] = useState('');
  const [touched, setTouched] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState('');
  const failed = 'The report may not have been resolved. Reload the list to check.';
  const { busy, send } = useIdempotentPost(failed);
  const note_err = payout_reason_error(note, 'note');

  const submit = () => {
    const body = claim_resolve_body(claim.claim_uuid, note);
    send(body, () => post_payout_claim_resolve(body)).then((r) => {
      if (r.ok) {
        on_done(`Report from ${claim.teacher_name} resolved.`);
        return;
      }
      if (r.err.kind === 'auth') on_error(r.raw, failed);
      if (r.err.kind === 'stale') on_stale(r.err.text);
      setError(r.err.text);
      setConfirming(false);
    });
  };

  return (
    <TpDialog
      title="Resolve payment not received"
      size="md"
      on_close={on_close}
      busy={busy}
      guard_text={discard_guard_text(note)}
    >
      <div className="tp-modal tp-po-claim-dlg">
        <div className="tp-ctx">
          {claim.teacher_name} · {claim.pay_month} · {claim.amount} sent {claim.sent_on}, UTR{' '}
          <span className="tp-mono">{claim.bank_reference}</span>
          {claim.note ? <div>Teacher wrote: &quot;{claim.note}&quot;</div> : null}
        </div>
        <div className="tp-field">
          <label htmlFor="tp-po-claim-note">What you found (recorded)</label>
          <textarea
            id="tp-po-claim-note"
            className="tp-textarea"
            value={note}
            maxLength={REASON_MAX_LEN * 2}
            onChange={(e) => {
              setConfirming(false);
              setNote(e.target.value);
            }}
            onBlur={() => setTouched(true)}
            placeholder="For example: bank confirmed the UTR was credited on 8 Nov; shared it with the teacher."
          />
          {touched && note_err ? (
            <span className="tp-neg" role="alert">
              {note_err}
            </span>
          ) : null}
        </div>
        {confirming ? (
          <div className="tp-banner tp-banner-warn" role="alert">
            Mark this report resolved? The teacher sees it as resolved.
          </div>
        ) : null}
        {error ? (
          <div className="tp-neg" role="alert">
            {error}
          </div>
        ) : null}
        <div className="tp-modal-ft">
          <button type="button" className="tp-btn-ghost" onClick={busy ? undefined : on_close}>
            Cancel
          </button>
          {confirming ? (
            <button
              type="button"
              className="tp-btn"
              onClick={busy ? undefined : submit}
              aria-disabled={busy ? 'true' : undefined}
            >
              {busy ? 'Saving…' : 'Yes, resolve'}
            </button>
          ) : (
            <button
              type="button"
              className="tp-btn-out"
              onClick={() => {
                setTouched(true);
                if (!note_err) setConfirming(true);
              }}
            >
              Resolve
            </button>
          )}
        </div>
      </div>
    </TpDialog>
  );
}

// Four tracks (list-row pattern): teacher and month · what was sent · amount · status and Resolve.
const claim_columns = (on_open_teacher, on_resolve) => {
  const resolve_button = (c, className) =>
    c.status === 'OPEN' ? (
      <button
        type="button"
        className={className}
        aria-label={`Resolve the report from ${c.teacher_name}`}
        onClick={() => on_resolve(c)}
      >
        Resolve
      </button>
    ) : null;
  const chip = (c) => <StatusPill tone={c.status_view.tone}>{c.status_view.label}</StatusPill>;
  const meta = (c) => [
    c.resolution_note ? `${c.resolution_note}${c.resolved_at ? ` (${c.resolved_at})` : ''}` : null
  ];
  return [
    {
      key: 'teacher',
      label: 'Teacher',
      role: 'identifier',
      track: { compact: '28%', full: '26%' },
      render: (c) => (
        <Who
          name={
            <button
              type="button"
              className="tp-link tp-po-name"
              aria-label={`Open ${c.teacher_name}'s ${c.pay_month} payouts`}
              onClick={() => on_open_teacher(c.teacher_uuid, c.pay_month_raw)}
            >
              {c.teacher_name}
            </button>
          }
          meta={[`Month ${c.pay_month}`, `Reported ${c.created_at}`]}
        />
      )
    },
    {
      key: 'sent',
      label: 'Payment',
      render: (c) => (
        <What
          title={`Sent ${c.sent_on}`}
          lines={[<span className="tp-mono">UTR {c.bank_reference}</span>]}
        />
      )
    },
    {
      key: 'amount',
      role: 'amount',
      label: 'Amount',
      align: 'right',
      track: { compact: '140px', full: '148px' },
      render: (c) => <span className="tp-c-amt">{c.amount}</span>,
      card_render: (c) => <Ledger items={[{ label: 'Amount', value: c.amount }]} />
    },
    {
      key: 'status',
      role: 'status',
      label: 'Status',
      track: { compact: '160px', full: '190px' },
      render: (c) => (
        <StatusCell
          chip={chip(c)}
          meta={meta(c)}
          actions={
            c.status === 'OPEN' ? (
              <span className="tp-c-acts tp-actions">{resolve_button(c, 'tp-link')}</span>
            ) : null
          }
        />
      ),
      card_render: (c) => <StatusCell chip={chip(c)} meta={meta(c)} />
    },
    {
      key: 'note',
      label: 'Teacher wrote',
      line: { from: 'sent', bare: true },
      render: (c) => <Note label="Teacher wrote" text={c.note || 'No note'} />
    },
    {
      key: 'action',
      label: 'Action',
      role: 'actions',
      in_table: false,
      card_render: (c) => resolve_button(c, 'tp-btn-out')
    }
  ];
};

export function ClaimsPanel({
  status = CLAIM_STATUS_DEFAULT,
  on_status: setStatus,
  on_open_teacher,
  on_notify,
  on_error
}) {
  const [claim, setClaim] = useState(null);
  const [stale, setStale] = useState('');
  const read = useCallback(
    (cursor) =>
      fetch_payout_claims(status, cursor).then((d) => {
        const v = claims_view(d);
        return { rows: v.rows, next: v.next_after_id };
      }),
    [status]
  );
  const list = useCursorList(read, 'Could not load the reports.', on_error, payout_error);

  return (
    <div className="tp-po-claims-panel">
      <p className="tp-po-lead">
        Teachers can report a sent payment as not received 2 bank working days after it was sent.
        Check the UTR with the bank, tell the teacher, then resolve.
      </p>
      <FilterBar label="Report filters" summary={summary_line(claims_filter_summary(status, list))}>
        <Segmented
          label="Report status"
          options={CLAIM_STATUSES}
          value={status}
          onChange={setStatus}
        />
        {claims_filters_on(status) ? (
          <button
            type="button"
            className="tp-link tp-fbar-end"
            onClick={() => setStatus(CLAIM_STATUS_DEFAULT)}
          >
            Clear filters
          </button>
        ) : null}
      </FilterBar>
      {stale ? (
        <div className="tp-banner tp-banner-warn" role="alert">
          {stale}
        </div>
      ) : null}
      <ListCard
        title="Reports"
        sub={shown_text(list)}
        acts={
          <DownloadCsvButton
            columns={CLAIM_COLUMNS}
            load_rows={() => collect_all_pages(read)}
            file_name={payouts_csv_name(`Payment not received ${status}`)}
            on_error={auth_only(payout_error, on_error)}
          />
        }
      >
        <CursorListBody
          list={list}
          render_rows={(rows) => (
            <DataTable
              columns={claim_columns(on_open_teacher, setClaim)}
              rows={rows}
              rowKey={(c) => c.claim_uuid}
              emptyText="No reports."
              card_below={660}
            />
          )}
        />
      </ListCard>
      {claim ? (
        <ClaimResolveDialog
          claim={claim}
          on_close={() => setClaim(null)}
          on_done={(text) => {
            setClaim(null);
            setStale('');
            on_notify('success', text);
            list.reload();
          }}
          on_stale={(text) => {
            setClaim(null);
            setStale(text);
            list.reload();
          }}
          on_error={on_error}
        />
      ) : null}
    </div>
  );
}

// ---- deleted accounts ----

const read_deleted = (cursor) =>
  fetch_payout_deleted_accounts(cursor).then((d) => {
    const v = deleted_view(d);
    return { rows: v.rows, next: v.next_after_teacher_id };
  });

// Three tracks: teacher · months still owed · total due.
const deleted_columns = (on_open_teacher) => [
  {
    key: 'teacher_label',
    role: 'identifier',
    label: 'Teacher',
    track: { compact: '30%', full: '28%' },
    render: (r) => (
      <Who name={r.teacher_label} meta={[`Last approved account ${r.account}`, r.branch_line]} />
    )
  },
  {
    key: 'months',
    label: 'Months',
    render: (r) => (
      <ul className="tp-po-months">
        {r.months.map((m) => (
          <li key={m.pay_month}>
            <button
              type="button"
              className="tp-link"
              aria-label={`Open ${r.teacher_label}'s ${m.pay_month} payouts`}
              onClick={() => on_open_teacher(r.teacher_uuid, m.pay_month)}
            >
              {m.pay_month}
            </button>{' '}
            <span className="tp-nowrap">{m.outstanding}</span>{' '}
            <span className="tp-sub">({m.lifecycle})</span>
          </li>
        ))}
      </ul>
    )
  },
  {
    key: 'total',
    role: 'amount',
    label: 'Total due',
    align: 'right',
    track: { compact: '140px', full: '148px' },
    render: (r) => <span className="tp-c-amt">{r.total}</span>,
    card_render: (r) => <Ledger items={[{ label: 'Total due', value: r.total }]} />
  }
];

export function DeletedPanel({ on_open_teacher, on_error }) {
  const list = useCursorList(
    read_deleted,
    'Could not load the deleted accounts.',
    on_error,
    payout_error
  );
  return (
    <div className="tp-po-deleted">
      <p className="tp-po-lead">
        Teachers who deleted their account while money is still due. They are never put in a bank
        file: pay the last approved account outside the file, then open the month and record the
        payment.
      </p>
      <ListCard
        title="Deleted accounts with money due"
        sub={shown_text(list)}
        acts={
          <DownloadCsvButton
            columns={DELETED_COLUMNS}
            load_rows={() =>
              collect_all_pages(read_deleted).then((res) => ({
                ...res,
                rows: deleted_csv_rows(res.rows)
              }))
            }
            file_name={payouts_csv_name('Deleted accounts')}
            on_error={auth_only(payout_error, on_error)}
          />
        }
      >
        <CursorListBody
          list={list}
          render_rows={(rows) => (
            <DataTable
              columns={deleted_columns(on_open_teacher)}
              rows={rows}
              rowKey={(r) => r.teacher_uuid}
              emptyText="No deleted account has money due."
              card_below={660}
            />
          )}
        />
      </ListCard>
    </div>
  );
}
